import axios from 'axios';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { GetSeatingChartResponse } from '../../../types';
import './style.css';
import { formatMarketplaceSection } from '../../../utils/formatters';
import { Box, Text, Tooltip } from '@mantine/core';
import { BrokerNerdsBarkerCoreEnumsMarketplace, BrokerNerdsBarkerCoreModelsMarketplacesSeatingChartZone } from '../../../api';
import { useHoverKeystrokes } from '../../hooks/useHoverKeystrokes';
import { checkDistance, findCenterOfUPath, getSectionIdComparisonRegex, matchSectionNumbers } from '../../../utils/svg-utils';
import { useGlobalState } from '../../../data/Global.state';
import fastdom from 'fastdom';
import { TenantIdListingId } from '../../../models/tenantIdListingId';
import { useFlag } from '@unleash/proxy-client-react';
import { useSeatingChart } from '../../../hoc/SeatingChart/SeatingChart.hooks';

const SECTION_BLACKLIST = ['stage', 'stqge'];

export type SvgSeatingChartProps = {
  tenantListingId: TenantIdListingId | undefined;
  selectedListingSection: string | null | undefined;
  selectedSections: string[] | null;
  onSectionClick: (sectionIds: string[]) => void;
  sectionsWithListings: string[] | null;
  suggestedSections: string[] | null;
  zoneMode: boolean;
  categoryMode: boolean;
  marketplaceId: BrokerNerdsBarkerCoreEnumsMarketplace;
  marketSectionAliases: string[] | undefined;
  markAllSections?: boolean;
  updateAllSectionsInSeatingChart: (sections: string[]) => void;
  advancedRowsEnabled?: boolean;
  advancedRowsActiveIndex?: number;
  advancedRows?: Map<string, string[]>;
} & Partial<GetSeatingChartResponse>;

function SvgSeatingChart({
  tenantListingId,
  selectedListingSection,
  selectedSections,
  sectionsWithListings,
  suggestedSections,
  onSectionClick,
  zones,
  svgUrl,
  zoneMode,
  categoryMode,
  marketplaceId,
  updateAllSectionsInSeatingChart,
  marketSectionAliases,
  markAllSections,
  advancedRowsEnabled,
  advancedRowsActiveIndex,
  advancedRows,
}: SvgSeatingChartProps) {
  const svgRef = useRef<HTMLDivElement>(null);
  const [tooltipLabel, setTooltipLabel] = useState<React.ReactNode>('');
  const [previousSvgUrl, setPreviousSvgUrl] = useState<string>();
  const [svgContent, setSvgContent] = useState<string>();
  const clickDragging = useRef(false);
  const dragSelectedSectionsRef = useRef<string[]>([]);
  const [dragSelectedSections, setDragSelectedSections] = useState<string[]>([]);
  const toggleDragRemovableRef = useRef(false);
  const selectedSectionsRef = useRef(selectedSections);
  const { isKeyPressedRef } = useHoverKeystrokes('Shift');
  const lastMousePositionRef = useRef<{ x: number; y: number } | undefined>(undefined);
  const sectionsInSeatingChartRef = useRef<Set<string>>(new Set());
  const {
    selectedMarketplaceEvent: { svgDetails },
    form,
  } = useSeatingChart('selectedMarketplaceEvent', 'form');

  // onSectionClick and form are recreated on every parent render, so mirror them into refs.
  // This keeps the DOM event-handler callbacks below stable (so they can be listed as effect
  // dependencies without re-wiring/looping) while still calling the latest version.
  const onSectionClickRef = useRef(onSectionClick);
  const formRef = useRef(form);

  // in React 19.2 there is a useEventEffect that creates a stable function that access current state to remove the need for mirroring into refs
  const zoneModeRef = useRef(zoneMode);
  const categoryModeRef = useRef(categoryMode);
  const dragging = useRef(false);
  const timer = useRef<NodeJS.Timeout | undefined>(undefined);
  const pathId = useMemo(() => {
    switch (marketplaceId) {
      case BrokerNerdsBarkerCoreEnumsMarketplace.Ticketmaster:
        return 'data-section-names';
      case BrokerNerdsBarkerCoreEnumsMarketplace.TicketEvolution:
        return 'data-section-id';
      case BrokerNerdsBarkerCoreEnumsMarketplace.Ticombo:
        return 'data-section';
      default:
        return 'id';
    }
  }, [marketplaceId]);
  const svgDetailsRef = useRef(svgDetails);

  const categoryPathId = 'data-category';

  useFlag('advanced-rows-input');

  useEffect(() => {
    selectedSectionsRef.current = selectedSections;
    zoneModeRef.current = zoneMode;
    categoryModeRef.current = categoryMode;
    svgDetailsRef.current = svgDetails;
    onSectionClickRef.current = onSectionClick;
    formRef.current = form;
  }, [selectedSections, zoneMode, categoryMode, svgDetails, onSectionClick, form]);

  useEffect(() => {
    const trackMouse = (e: MouseEvent) => {
      lastMousePositionRef.current = { x: e.clientX, y: e.clientY };
    };
    document.addEventListener('mousemove', trackMouse);
    return () => {
      document.removeEventListener('mousemove', trackMouse);
    };
  }, []);

  useEffect(() => {
    fastdom.mutate(() => {
      svgRef.current
        ?.querySelector('svg')
        ?.querySelectorAll('circle')
        .forEach((x) => x.remove());
    });
  }, [selectedListingSection]);

  useEffect(() => {
    if (svgUrl && svgUrl !== previousSvgUrl) {
      setPreviousSvgUrl(svgUrl);
      axios
        .get(svgUrl)
        .catch((_error) => {})
        .then((response) => {
          const svgRaw = response?.data.substr(response.data.indexOf('<svg'));
          setSvgContent(svgRaw);
        });
    }
  }, [svgUrl, previousSvgUrl]);

  const onMouseout = useCallback(
    (paths: SVGElement[]) =>
      function handleMouseout(_event: MouseEvent) {
        paths.forEach((p) => {
          p.classList.remove('zone-hover');
          p.classList.remove('section-hover');
          p.classList.remove('active-section-hover');
          setTooltipLabel('');
        });
      },
    [],
  );

  const onMousedown = useCallback(
    (event: MouseEvent) => {
      clickDragging.current = true;
      timer.current = setTimeout(() => (dragging.current = true), 200);
      const target = (marketplaceId === BrokerNerdsBarkerCoreEnumsMarketplace.Ticombo ? event.currentTarget : event.target) as SVGElement;
      let section = target.getAttribute(pathId)?.replaceAll('-', ' ');
      if (!section) {
        section = target.getAttribute(categoryPathId)?.replaceAll('-', ' ');
      }
      if (isKeyPressedRef.current && section) {
        if (selectedSectionsRef.current?.includes(section)) {
          toggleDragRemovableRef.current = true;
        } else {
          toggleDragRemovableRef.current = false;
        }
        if (svgDetailsRef.current?.categories && categoryModeRef.current) {
          for (const c of svgDetailsRef.current.categories) {
            const formattedCatSectionId = c.sectionId.replaceAll('-', ' ');
            const mappedSections = c.sections.map((s) => `${formattedCatSectionId}_${s.toLowerCase().replaceAll('-', ' ')}`);
            if (mappedSections.some((s) => s === section)) {
              dragSelectedSectionsRef.current.push(...mappedSections.filter((mappedSection) => sectionsInSeatingChartRef.current.has(mappedSection)), formattedCatSectionId);
            }
          }
        } else {
          dragSelectedSectionsRef.current.push(section);
        }
        setDragSelectedSections([...dragSelectedSectionsRef.current]);
      }
    },
    [pathId, isKeyPressedRef, marketplaceId],
  );

  const onMouseup = useCallback(() => {
    clickDragging.current = false;
    if (timer.current) {
      clearTimeout(timer.current);
    }
    dragging.current = false;
    if (dragSelectedSectionsRef.current.length > 0) {
      const dragSet = new Set(structuredClone(dragSelectedSectionsRef.current));
      const formSectionIds = formRef.current.getValues().sectionIds;

      if (svgDetailsRef.current?.categories && categoryModeRef.current) {
        for (const c of svgDetailsRef.current.categories) {
          const formattedCatSectionId = c.sectionId.replaceAll('-', ' ');
          const mappedSections = c.sections.map((s) => `${formattedCatSectionId}_${s.toLowerCase()}`);
          if (mappedSections.some((s) => dragSet.has(s))) {
            mappedSections.forEach((s) => {
              // Avoid adding unmapped sections to when drag selecting a category
              if (sectionsInSeatingChartRef.current.has(s)) {
                dragSet.add(s);
              }
            });
            dragSet.add(c.sectionId.toLowerCase().replaceAll('-', ' '));
          }
        }
      } else if (svgDetailsRef.current?.categories && formSectionIds && formSectionIds.length > 0) {
        const fromIdsSet = new Set(formSectionIds);
        const selectedCategories = svgDetailsRef.current.categories.filter((c) => fromIdsSet.has(c.sectionId.toLowerCase().replaceAll('-', ' ')));
        for (const c of selectedCategories) {
          const formattedCatSectionId = c.sectionId.replaceAll('-', ' ');
          const mappedSections = c.sections.map((s) => `${formattedCatSectionId}_${s.toLowerCase()}`);
          if (mappedSections.some((s) => dragSet.has(s))) {
            mappedSections.forEach((s) => {
              if (sectionsInSeatingChartRef.current.has(s)) {
                dragSet.add(s);
              }
            });
            dragSet.add(c.sectionId.toLowerCase().replaceAll('-', ' '));
          }
        }
      }

      onSectionClickRef.current(Array.from(dragSet));
      dragSelectedSectionsRef.current = [];
      setDragSelectedSections([]);
    }
  }, []);

  const findSectionInAdvancedRows = useCallback(
    (sectionId: string) => Array.from(advancedRows?.entries() ?? []).find(([_keys, values]) => values.includes(sectionId))?.[0] ?? '',
    [advancedRows],
  );

  const onPathClick = useCallback(
    (zone: BrokerNerdsBarkerCoreModelsMarketplacesSeatingChartZone, section: string) =>
      function handleClick(event: MouseEvent) {
        if (dragging.current) {
          event.stopPropagation();
          return;
        }

        if (zoneModeRef.current && zone) {
          onSectionClickRef.current(zone.sections.map((x) => x.replaceAll('-', ' ')));
        } else if (categoryModeRef.current && svgDetailsRef.current?.categories) {
          for (const c of svgDetailsRef.current.categories) {
            const formattedCatSectionId = c.sectionId.replaceAll('-', ' ');
            let mappedSections = c.sections.map((s) => `${formattedCatSectionId}_${s.toLowerCase()}`);

            if (section === formattedCatSectionId || (event.currentTarget as SVGElement)?.getAttribute(pathId) === `${c.sectionId}_` || mappedSections.some((s) => s === section)) {
              // Don't add unmapped sections when clicking a category
              mappedSections = mappedSections.filter((mappedSection) => sectionsInSeatingChartRef.current.has(mappedSection));
              mappedSections.push(c.sectionId.toLowerCase().replaceAll('-', ' '));
              onSectionClickRef.current(mappedSections);
              break;
            }
          }
        } else {
          let selectedCategorySections: string[] = [];
          const formSectionIds = formRef.current.getValues().sectionIds;
          if (svgDetailsRef.current?.categories && formSectionIds && formSectionIds.length > 0) {
            const fromIdsSet = new Set(formSectionIds);
            const selectedCategories = svgDetailsRef.current.categories.filter((c) => fromIdsSet.has(c.sectionId.toLowerCase().replaceAll('-', ' ')));
            for (const c of selectedCategories) {
              const formattedCatSectionId = c.sectionId.replaceAll('-', ' ');
              const mappedSections = c.sections.map((s) => `${formattedCatSectionId}_${s.toLowerCase()}`);
              if (mappedSections.some((s) => s === section)) {
                selectedCategorySections = mappedSections.filter((mappedSection) => sectionsInSeatingChartRef.current.has(mappedSection));
                selectedCategorySections.push(c.sectionId.toLowerCase().replaceAll('-', ' '));
                break;
              }
            }
          }

          if (selectedCategorySections.length > 0) {
            // category is selected, so deselect the entire category
            onSectionClickRef.current(selectedCategorySections);
          } else {
            onSectionClickRef.current(section.replaceAll('-', ' ').split(','));
          }
        }
      },
    [pathId],
  );

  const onSvgClick = useCallback((_event: MouseEvent) => {
    if (svgRef.current) {
      const svg = svgRef.current.querySelector('svg')!;
      svg.focus();
    }
  }, []);

  const onMouseover = useCallback(
    (paths: SVGElement[], path: SVGElement, section: string, zone: BrokerNerdsBarkerCoreModelsMarketplacesSeatingChartZone | undefined) =>
      function handleMouseover(event: MouseEvent) {
        if (clickDragging.current && isKeyPressedRef.current && (categoryModeRef.current || !path.getAttribute(categoryPathId))) {
          if (categoryModeRef.current && svgDetailsRef.current?.categories) {
            for (const c of svgDetailsRef.current.categories) {
              const formattedCatSectionId = c.sectionId.replaceAll('-', ' ');
              const mappedSections = c.sections.map((s) => `${formattedCatSectionId}_${s.toLowerCase().replaceAll('-', ' ')}`);
              if (mappedSections.some((s) => s === section)) {
                dragSelectedSectionsRef.current.push(...mappedSections.filter((mappedSection) => sectionsInSeatingChartRef.current.has(mappedSection)), formattedCatSectionId);
              }
            }
          } else {
            dragSelectedSectionsRef.current.push(section);
          }
          setDragSelectedSections([...dragSelectedSectionsRef.current]);
        }

        if (marketplaceId === BrokerNerdsBarkerCoreEnumsMarketplace.Ticketmaster) {
          const sectionIdLabel = path.getAttribute('data-section-id')!;
          setTooltipLabel(formatMarketplaceSection(sectionIdLabel));
        } else if (marketplaceId === BrokerNerdsBarkerCoreEnumsMarketplace.Ticombo) {
          const catId = path.getAttribute(categoryPathId) ?? '';
          if (
            !svgDetailsRef.current?.categories?.length ||
            (!catId && !categoryModeRef.current && !selectedSectionsRef.current?.includes(path.parentElement?.getAttribute(categoryPathId) ?? ''))
          ) {
            const sectionParts = section.split('_');
            setTooltipLabel(`${formatMarketplaceSection(sectionParts[0])}${sectionParts.length > 1 ? ` ${sectionParts[1].toUpperCase()}` : ''}`);
          } else if ((catId && categoryModeRef.current) || selectedSectionsRef.current?.includes(catId)) {
            const catLabel = svgDetailsRef.current?.categories?.find((cat) => catId === cat.sectionId)?.name ?? '';
            setTooltipLabel(formatMarketplaceSection(catLabel));
          }
        } else {
          const tooltipLabel = advancedRowsEnabled ? findSectionInAdvancedRows(section) : formatMarketplaceSection(section);
          setTooltipLabel(
            advancedRowsEnabled ? (
              <Box className="tooltipText">
                <Text>{formatMarketplaceSection(section)}</Text>
                {tooltipLabel && (
                  <Text fz="10" className="rows">
                    {tooltipLabel ? `Rows: ${tooltipLabel}` : ''}
                  </Text>
                )}
              </Box>
            ) : (
              formatMarketplaceSection(section)
            ),
          );
        }

        paths.forEach((p) => {
          const section = p.getAttribute(pathId)!;
          if (zone?.sections.some((x) => section.split(',').includes(x))) {
            p.classList.add('zone-hover');
          }
          if (section && section === (event.currentTarget as SVGElement).getAttribute(pathId)!) {
            p.classList.add('section-hover');
            if (selectedSectionsRef.current?.includes(section!)) {
              p.classList.add('active-section-hover');
            }
          }
        });
      },
    [advancedRowsEnabled, pathId, isKeyPressedRef, findSectionInAdvancedRows, marketplaceId],
  );

  const sectionsPerRows = useMemo(() => Object.values(Object.fromEntries(advancedRows ?? new Map<string, string[]>())), [advancedRows]);

  // List every section from sectionsPerRow and which index it appears in
  const sectionsPerRowsIndices = useMemo(() => sectionsPerRows.flatMap((sections, index) => sections.map((section) => ({ section, index }))), [sectionsPerRows]);

  const svgMouseover = useCallback(() => {
    requestAnimationFrame(() => {
      svgRef.current?.classList.remove('suggest-section-animation');
    });
  }, []);
  const svgMouseout = useCallback(() => {
    svgRef.current?.classList.add('suggest-section-animation');
  }, []);

  useEffect(() => {
    // Handles wiring up the event handlers and disposing of them only
    const pathOnMouseOut = new Map<SVGElement, (event: MouseEvent) => void>();
    const pathOnClick = new Map<SVGElement, (event: MouseEvent) => void>();
    const pathOnMouseOver = new Map<SVGElement, (event: MouseEvent) => void>();

    const container = svgRef.current;
    if (svgContent && container) {
      container.innerHTML = svgContent;
      const svg = container.querySelector('svg')!;
      const venueWrapper = document.getElementById('venueWrapper')!;
      svg.addEventListener('mouseenter', svgMouseover);
      venueWrapper.addEventListener('mouseenter', svgMouseover);
      venueWrapper.addEventListener('mouseleave', svgMouseout);
      let paths = [...svg.querySelectorAll(`[${pathId}]`)] as SVGElement[];
      let categoryPaths = [...svg.querySelectorAll(`[${categoryPathId}]`)] as SVGElement[];

      const unusedCategories = new Set<string>();
      if (svgDetails?.categories && svgDetails.categories.length > 0) {
        // Sometimes we get an empty category list despite categories being present
        const usedCategoryIds = new Set(svgDetails.categories.map((c) => c.sectionId.replaceAll('-', ' ').toLowerCase()));
        categoryPaths = categoryPaths.filter((path) => {
          const categoryId = path.getAttribute(categoryPathId)?.replaceAll('-', ' ').toLowerCase();
          if (svgDetails?.categories?.length && categoryId && !usedCategoryIds.has(categoryId)) {
            unusedCategories.add(categoryId);
            path.setAttribute('data-unused', '');
            return false;
          }
          return true;
        });
      }

      if (marketplaceId === BrokerNerdsBarkerCoreEnumsMarketplace.Ticombo && categoryPaths.length > 0) {
        // filter out paths that are the only section in a category
        paths = paths.filter((p) => !p.parentElement?.getAttribute(categoryPathId) || p.parentElement.children.length > 1);
      }

      if (marketplaceId === BrokerNerdsBarkerCoreEnumsMarketplace.Ticombo) {
        paths = [...paths, ...categoryPaths] as SVGElement[];
      }

      const text = svg.querySelectorAll('text');
      const tspan = svg.querySelectorAll('tspan');

      if (marketplaceId === BrokerNerdsBarkerCoreEnumsMarketplace.Ticketmaster) {
        const basePaths = ([...svg.getElementsByTagName('path')] as SVGElement[]).filter((p) => p.parentElement?.tagName === 'svg');
        basePaths.forEach((p) => {
          p.style.pointerEvents = 'none';
        });
      }

      // Note: This is mostly for the tevo svg (also the Ticketmaster one). Since the numbers are ontop of the text
      text.forEach((t) => {
        if (t) {
          t.style.pointerEvents = 'none';

          // This is special logic for Ticketmaster so we don't change the color of base-level text
          if (t.parentElement?.classList.contains('label')) {
            t.setAttribute('fill', 'var(--colors-paperReverse)');
          }
        }
      }); // Needed to make the text not block the click events and use underlying section paths

      // Note: This is for the Ticketmaster SVG so the labels line up correctly to their x/y coordinates
      tspan.forEach((t) => {
        if (t && t.parentElement?.classList.contains('label')) {
          t.style.textAnchor = 'middle';
        }
      });

      const sections: string[] = paths.flatMap((path) => {
        let section = path.getAttribute(pathId)?.replaceAll('-', ' ');
        if (!section) {
          section = path.getAttribute(categoryPathId)?.replaceAll('-', ' ');
        }
        if (!section || SECTION_BLACKLIST.includes(section.toLowerCase())) {
          return [];
        }

        // Remove invalid Ticombo paths for categories
        if (marketplaceId === BrokerNerdsBarkerCoreEnumsMarketplace.Ticombo) {
          const categoryId = (path.parentElement?.getAttribute(categoryPathId) || path.getAttribute(categoryPathId))?.replaceAll('-', ' ');

          if (section === `${categoryId}_`) {
            return [];
          }
        }

        const zone = zones?.find((x) => x.sections.some((x) => section!.replaceAll(' ', '-').split(',').includes(x)));

        pathOnMouseOver.set(path, onMouseover(paths, path, section, zone));
        path.addEventListener('mouseover', pathOnMouseOver.get(path)!);

        pathOnMouseOut.set(path, onMouseout(paths));
        path.addEventListener('mouseout', pathOnMouseOut.get(path)!);
        const category = path.getAttribute(categoryPathId);
        const allowClickCategory =
          categoryModeRef.current ||
          (category && svgDetails?.categories?.length && svgDetails?.categories?.some((x) => x.sectionId === category && (!x.sections || x.sections.length < 2)));
        if (!category || allowClickCategory) {
          pathOnClick.set(path, onPathClick(zone!, section));
          path.addEventListener('click', pathOnClick.get(path)!);

          path.addEventListener('mousedown', onMousedown);
          path.addEventListener('mouseup', onMouseup);
        }
        svg.addEventListener('click', onSvgClick);

        if (marketplaceId === BrokerNerdsBarkerCoreEnumsMarketplace.Ticketmaster) {
          return section?.split(',') ?? [];
        }
        return section;
      });

      updateAllSectionsInSeatingChart(sections);
      sectionsInSeatingChartRef.current = new Set(sections);

      svg.setAttribute('tabindex', '0');
      svg.setAttribute('width', '450');
      svg.setAttribute('height', '450');
    }

    return () => {
      const svg = container?.querySelector('svg');
      if (svg) {
        // venueWrapper is an ancestor; it may already be gone from the DOM during unmount.
        const venueWrapper = document.getElementById('venueWrapper');
        // svgMouseover(); // Not sure I want to do this yet.
        svg.removeEventListener('mouseenter', svgMouseover);
        venueWrapper?.removeEventListener('mouseenter', svgMouseover);
        venueWrapper?.removeEventListener('mouseleave', svgMouseout);
        const paths = [...svg.querySelectorAll(`[${pathId}]`)] as SVGElement[];

        if (marketplaceId === BrokerNerdsBarkerCoreEnumsMarketplace.Ticketmaster) {
          const basePaths = ([...svg.getElementsByTagName('path')] as SVGElement[]).filter((p) => p.parentElement?.tagName === 'svg');
          basePaths.forEach((p) => {
            fastdom.mutate(() => {
              p.style.pointerEvents = 'none';
            });
          });
        }

        paths.forEach((path) => {
          path.removeEventListener('mouseover', pathOnMouseOver.get(path)!);
          path.removeEventListener('mouseout', pathOnMouseOut.get(path)!);
          path.removeEventListener('click', pathOnClick.get(path)!);
          path.removeEventListener('mousedown', onMousedown);
          path.removeEventListener('mouseup', onMouseup);
          svg.removeEventListener('click', onSvgClick);
          svg.removeEventListener('mousemove', checkDistance);
        });
      }
    };
  }, [
    svgContent,
    onMouseover,
    onMouseout,
    onMousedown,
    onMouseup,
    onPathClick,
    onSvgClick,
    svgMouseover,
    svgMouseout,
    updateAllSectionsInSeatingChart,
    pathId,
    marketplaceId,
    zones,
    svgDetails,
  ]);

  useEffect(() => {
    if (svgContent && svgRef.current) {
      const svg = svgRef.current.querySelector('svg')!;
      let paths = [...svg.querySelectorAll(`[${pathId}]`), ...svg.querySelectorAll(`[${categoryPathId}]`)] as SVGElement[];

      if (marketplaceId === BrokerNerdsBarkerCoreEnumsMarketplace.Ticketmaster) {
        const basePaths = paths.filter((p) => p.parentElement?.tagName === 'svg');
        basePaths.forEach((p) => {
          p.style.pointerEvents = 'none';
        });

        paths = paths.filter((p) => p.parentElement?.id === 'polygons');
      } // For Ticketmaster, ignore any paths that aren't part of the polygons group, since they are part of the underlying image and not the overlay

      const allSections = paths.map((path) => (path.getAttribute(pathId) || path.getAttribute(categoryPathId))?.replaceAll('-', ' ')).filter((x) => x) as string[];

      let found: string[] = [];
      try {
        if (selectedListingSection) {
          const regex = new RegExp(`(?<!\\d)${selectedListingSection}(?!\\d)`);
          found = allSections.filter((x) => regex.test(x));
        }
      } catch {
        if (selectedListingSection) {
          found = allSections.filter((x) => matchSectionNumbers(x, selectedListingSection));
        }
      }

      paths.forEach((path) => {
        const section = (path.getAttribute(pathId) || path.getAttribute(categoryPathId))?.replaceAll('-', ' ');
        const sections = (path.getAttribute(pathId) || path.getAttribute(categoryPathId))?.replaceAll('-', ' ').split(',') ?? [];
        if (SECTION_BLACKLIST.includes(section?.toLowerCase() ?? '')) {
          return;
        }

        const sectionMatchRegex = getSectionIdComparisonRegex(section);

        if (
          sections.includes(selectedListingSection?.toLowerCase().replaceAll('-', ' ')!) ||
          (found.length == 1 && found[0] === section) ||
          (Array.isArray(marketSectionAliases) && marketSectionAliases.some((x) => x.toLowerCase().replaceAll('-', ' ') === section?.toLowerCase().replaceAll('-', ' ')!))
        ) {
          // const centerPosition = findPathCenter(path);
          const centerPosition = findCenterOfUPath(path);
          // const centerPosition = findApproximateCenterOfUPath(path);
          // const centerPosition = findCenterXCenterYPath(path);
          // const centerPosition = findCenterPointOnPath(path);
          // const centerPosition = findBottomCenterPath(path);
          // const centerPosition = getCenterOfSvgPathWithTransform(getDomPathToSVGElement(path));
          // const centerPosition = findRectangleCenters(path.getAttribute('d')!);
          if (centerPosition) {
            const countCenters: { x: number; y: number }[] = []; //findRectangleCenters(getUniqueSelector(path));
            if (countCenters.length > 2 && !countCenters.some((x) => isNaN(x.x) || isNaN(x.y))) {
              countCenters.map((centerPosition) => {
                const circle = document.createElementNS('http://www.w3.org/2000/svg', 'circle');
                circle.setAttribute('cx', centerPosition.x.toString());
                circle.setAttribute('cy', centerPosition.y.toString());
                circle.setAttribute('r', marketplaceId === BrokerNerdsBarkerCoreEnumsMarketplace.Ticketmaster ? `${200}px` : `${10}px`); // radius of the circle
                circle.setAttribute('fill', 'var(--colors-red-error)');
                circle.setAttribute('pointer-events', 'none');
                fastdom.mutate(() => {
                  svgRef.current?.querySelector('svg')?.appendChild(circle);
                });
              });
            } else {
              const circle = document.createElementNS('http://www.w3.org/2000/svg', 'circle');
              circle.setAttribute('cx', centerPosition.x.toString());
              circle.setAttribute('cy', centerPosition.y.toString());
              circle.setAttribute('r', marketplaceId === BrokerNerdsBarkerCoreEnumsMarketplace.Ticketmaster ? `${100}px` : `${10}px`); // radius of the circle
              circle.setAttribute('fill', 'var(--colors-red-error)');
              circle.setAttribute('pointer-events', 'none');
              fastdom.mutate(() => {
                svgRef.current?.querySelector('svg')?.appendChild(circle);
              });
            }
            svg.addEventListener('mousemove', checkDistance);
          }
        }

        // Clean slate
        const classes = new Set<string>();
        classes.add('section');
        if (section) {
          const splitSections = section.split(',').map((x) => x.toLowerCase().replaceAll('-', ' '));
          if (markAllSections || sectionsWithListings?.some((x) => splitSections.includes(x.toLowerCase()))) {
            classes.add('has-listings');
          } else if (svgDetailsRef.current?.categories && svgDetailsRef.current.categories.length > 0) {
            const sectionCategory = svgDetailsRef.current.categories.find((c) => section.includes(c.sectionId.replaceAll('-', ' ')));
            if (sectionCategory) {
              if (sectionsWithListings?.some((x) => x.toLowerCase() === section.replace(`${sectionCategory.sectionId.replaceAll('-', ' ')}_`, ''))) {
                classes.add('has-listings');
              }
            }
          }
          if (
            suggestedSections?.some((x) => splitSections.includes(x.toLowerCase())) &&
            (selectedSections?.length ?? 0) === 0 &&
            Object.values(advancedRows ?? {})
              .flatMap((x) => x)
              .filter((x) => x !== '').length === 0
          ) {
            classes.add('suggest-section');
          }
          if (toggleDragRemovableRef.current) {
            if (selectedSections?.some((s) => sectionMatchRegex?.test(s)) && !dragSelectedSectionsRef.current.some((s) => sectionMatchRegex?.test(s))) {
              classes.add('active-section');
            } else if (sectionsPerRowsIndices.some((x) => x.section === section) && !dragSelectedSectionsRef.current.includes(section)) {
              classes.add('active-section');
              classes.add(`rows-${sectionsPerRowsIndices.find((x) => x.section === section)?.index}`);
            }
          } else if (selectedSections?.some((s) => sectionMatchRegex?.test(s)) || dragSelectedSectionsRef.current.some((s) => sectionMatchRegex?.test(s))) {
            classes.add('active-section');
            classes.add(`rows-${advancedRowsActiveIndex}`);
          } else if (sectionsPerRowsIndices.some((x) => x.section === section)) {
            classes.add('active-section');
            classes.add(`rows-${sectionsPerRowsIndices.find((x) => x.section === section)?.index}`);
          }
          if (marketplaceId === BrokerNerdsBarkerCoreEnumsMarketplace.Ticketmaster) {
            const sectionNames = section.split(',').map((x) => x.replaceAll('-', ' ')) || [];
            if (sectionNames.some((x) => selectedSections?.includes(x))) {
              classes.add('active-section');
            } else if (sectionNames.every((x) => sectionsPerRowsIndices.some((y) => y.section === x))) {
              classes.add('active-section');
              classes.add(`rows-${sectionsPerRowsIndices.find((x) => sectionNames.includes(x.section))?.index}`);
            }
          }
        }
        fastdom.mutate(() => {
          path.classList.add(...classes.keys());
          path.classList.forEach((c) => {
            if (!Array.from(classes).includes(c) && c !== 'zone-hover') {
              path.classList.remove(c);
            }
          });
        });
      });
      return () => {
        svg.removeEventListener('mousemove', checkDistance);
      };
    }
  }, [
    advancedRowsActiveIndex,
    svgContent,
    selectedSections,
    selectedListingSection,
    sectionsWithListings,
    suggestedSections,
    zoneMode,
    dragSelectedSections,
    marketSectionAliases,
    sectionsPerRowsIndices,
    markAllSections,
    advancedRows,
    pathId,
    marketplaceId,
  ]);

  useEffect(() => {
    requestAnimationFrame(() => {
      svgRef.current?.classList.remove('suggest-section-animation');
    });
  }, [tenantListingId]);

  const { isMobile } = useGlobalState();

  return (
    <Tooltip.Floating label={tooltipLabel} fz="xs" style={{ textTransform: 'capitalize' }} disabled={tooltipLabel === '' || isMobile} bg={'var(--colors-gray-7)'}>
      <div
        onMouseLeave={() => {
          if (dragSelectedSectionsRef.current.length > 0) {
            onMouseup();
          }
        }}
        /* .ticombo is a temporary class designation while working through chart styles */
        className={`${zoneMode ? 'bnSeatingChart zoneSelect' : 'bnSeatingChart'} ${(suggestedSections?.length ?? 0) > 0 ? 'suggest-section-animation' : ''}`}
        ref={svgRef}
        id="seatingChartSVG"
      />
    </Tooltip.Floating>
  );
}

export default SvgSeatingChart;
